import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Trash2, Edit2, DollarSign, X, Save, Clock, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PAYMENT_TYPES = [
  { id: 'flat_rate', name: 'Flat Rate', icon: DollarSign },
  { id: 'hourly', name: 'Hourly', icon: Clock },
  { id: 'commission', name: 'Commission', icon: Percent },
  { id: 'manual', name: 'Manual Entry', icon: DollarSign },
];

export default function StaffEarnings({ embedded = false }) {
  const [earnings, setEarnings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEarning, setEditingEarning] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [staffSummary, setStaffSummary] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    staff_id: '',
    event_id: '',
    payment_type: 'flat_rate',
    amount: '',
    hours_worked: '',
    hourly_rate: '',
    commission_percentage: '',
    commission_base: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, [selectedStaff]);

  useEffect(() => {
    if (selectedStaff) {
      fetchStaffSummary(selectedStaff);
    } else {
      setStaffSummary(null);
    }
  }, [selectedStaff]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [earningsRes, staffRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/staff-earnings${selectedStaff ? `?staff_id=${selectedStaff}` : ''}`, { headers }),
        axios.get(`${BACKEND_URL}/api/users/staff`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers })
      ]);
      
      setEarnings(earningsRes.data);
      setStaff(staffRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffSummary = async (staffId) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/staff-earnings/summary/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary');
    }
  };

  const calculateAmount = () => {
    const { payment_type, hours_worked, hourly_rate, commission_percentage, commission_base, amount } = formData;
    
    if (payment_type === 'hourly' && hours_worked && hourly_rate) {
      return parseFloat(hours_worked) * parseFloat(hourly_rate);
    }
    if (payment_type === 'commission' && commission_percentage && commission_base) {
      return (parseFloat(commission_percentage) / 100) * parseFloat(commission_base);
    }
    return parseFloat(amount) || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.staff_id || !formData.date) {
      toast.error('Please select staff member and date');
      return;
    }

    const calculatedAmount = calculateAmount();
    if (calculatedAmount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    const payload = {
      ...formData,
      amount: calculatedAmount,
      hours_worked: formData.hours_worked ? parseFloat(formData.hours_worked) : null,
      hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      commission_percentage: formData.commission_percentage ? parseFloat(formData.commission_percentage) : null,
      commission_base: formData.commission_base ? parseFloat(formData.commission_base) : null,
      event_id: formData.event_id || null
    };

    try {
      const token = localStorage.getItem('vyrus_token');
      if (editingEarning) {
        await axios.put(`${BACKEND_URL}/api/staff-earnings/${editingEarning.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Earning updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/staff-earnings`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Earning recorded');
      }
      
      setShowForm(false);
      setEditingEarning(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (earningId) => {
    if (!window.confirm('Delete this earning record?')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/staff-earnings/${earningId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (earning) => {
    setEditingEarning(earning);
    setFormData({
      staff_id: earning.staff_id,
      event_id: earning.event_id || '',
      payment_type: earning.payment_type,
      amount: earning.amount,
      hours_worked: earning.hours_worked || '',
      hourly_rate: earning.hourly_rate || '',
      commission_percentage: earning.commission_percentage || '',
      commission_base: earning.commission_base || '',
      description: earning.description || '',
      date: earning.date
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      staff_id: selectedStaff || '',
      event_id: '',
      payment_type: 'flat_rate',
      amount: '',
      hours_worked: '',
      hourly_rate: '',
      commission_percentage: '',
      commission_base: '',
      description: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStaffName = (staffId) => {
    const member = staff.find(s => s.id === staffId);
    return member?.name || member?.email || 'Unknown';
  };

  const getEventName = (eventId) => {
    if (!eventId) return 'General';
    const event = events.find(e => e.id === eventId);
    return event?.name || 'Unknown Event';
  };

  const totalEarnings = earnings.reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-20">
      {!embedded && (
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight">Staff Earnings</h1>
          <p className="text-muted-foreground mt-1 text-sm">Track payments to staff members</p>
        </div>
      )}

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Paid {selectedStaff ? `to ${getStaffName(selectedStaff)}` : '(All Staff)'}</p>
              <p className="text-3xl font-bold text-primary">{formatCurrency(staffSummary?.total_earnings || totalEarnings)}</p>
            </div>
            {staffSummary && (
              <div className="text-sm">
                <p className="text-muted-foreground">{staffSummary.earning_records} payments</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <select
          value={selectedStaff}
          onChange={(e) => setSelectedStaff(e.target.value)}
          className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
        >
          <option value="">All Staff</option>
          {staff.map(member => (
            <option key={member.id} value={member.id}>{member.name || member.email}</option>
          ))}
        </select>
        
        <Button onClick={() => { resetForm(); setShowForm(true); setEditingEarning(null); }} className="ml-auto">
          <Plus className="h-4 w-4 mr-2" /> Record Payment
        </Button>
      </div>

      {/* Staff Summary by Type */}
      {staffSummary && staffSummary.by_payment_type && Object.keys(staffSummary.by_payment_type).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(staffSummary.by_payment_type).map(([type, amount]) => (
            <Card key={type} className="bg-card border-border/50">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground capitalize">{type.replace('_', ' ')}</p>
                <p className="text-lg font-bold">{formatCurrency(amount)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Earnings List */}
      <div className="space-y-3">
        {earnings.length === 0 ? (
          <Card className="p-8 text-center bg-card border-border/50">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No earnings recorded yet</p>
          </Card>
        ) : (
          earnings.map((earning) => (
            <Card key={earning.id} className="bg-card border-border/50">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{getStaffName(earning.staff_id)}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary capitalize">
                        {earning.payment_type?.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{getEventName(earning.event_id)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(earning.date).toLocaleDateString()}
                      {earning.description && ` • ${earning.description}`}
                    </p>
                    {earning.hours_worked && (
                      <p className="text-xs text-muted-foreground">
                        {earning.hours_worked}h × {formatCurrency(earning.hourly_rate)}/hr
                      </p>
                    )}
                    {earning.commission_percentage && (
                      <p className="text-xs text-muted-foreground">
                        {earning.commission_percentage}% of {formatCurrency(earning.commission_base)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-lg font-bold text-green-500 font-mono">{formatCurrency(earning.amount)}</p>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(earning)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(earning.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-card">
            <CardHeader className="border-b border-border flex-row items-center justify-between">
              <CardTitle>{editingEarning ? 'Edit Payment' : 'Record Payment'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingEarning(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Staff Member *</label>
                  <select
                    value={formData.staff_id}
                    onChange={(e) => setFormData({...formData, staff_id: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    required
                  >
                    <option value="">Select Staff</option>
                    {staff.map(member => (
                      <option key={member.id} value={member.id}>{member.name || member.email}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Event (Optional)</label>
                  <select
                    value={formData.event_id}
                    onChange={(e) => setFormData({...formData, event_id: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                  >
                    <option value="">General (No Event)</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>{event.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Payment Type *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {PAYMENT_TYPES.map(type => (
                      <Button
                        key={type.id}
                        type="button"
                        variant={formData.payment_type === type.id ? 'default' : 'outline'}
                        className="justify-start"
                        onClick={() => setFormData({...formData, payment_type: type.id, amount: '', hours_worked: '', hourly_rate: '', commission_percentage: '', commission_base: ''})}
                      >
                        <type.icon className="h-4 w-4 mr-2" />
                        {type.name}
                      </Button>
                    ))}
                  </div>
                </div>

                {formData.payment_type === 'flat_rate' && (
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                        required
                      />
                    </div>
                  </div>
                )}

                {formData.payment_type === 'hourly' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Hours *</label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={formData.hours_worked}
                        onChange={(e) => setFormData({...formData, hours_worked: e.target.value})}
                        className="bg-secondary/50 border-transparent focus:border-primary"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Rate/Hr *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.hourly_rate}
                          onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                          className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {formData.payment_type === 'commission' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">% *</label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={formData.commission_percentage}
                          onChange={(e) => setFormData({...formData, commission_percentage: e.target.value})}
                          className="bg-secondary/50 border-transparent focus:border-primary"
                          required
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Base Amount *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.commission_base}
                          onChange={(e) => setFormData({...formData, commission_base: e.target.value})}
                          className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {formData.payment_type === 'manual' && (
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                        required
                      />
                    </div>
                  </div>
                )}

                {calculateAmount() > 0 && (
                  <div className="text-right p-3 bg-green-500/10 rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Payment</p>
                    <p className="text-xl font-bold text-green-500">{formatCurrency(calculateAmount())}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Date *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="bg-secondary/50 border-transparent focus:border-primary"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Description</label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Day 1 shift, Bonus, etc."
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>

                <Button type="submit" className="w-full">
                  <Save className="h-4 w-4 mr-2" /> {editingEarning ? 'Update' : 'Record'} Payment
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
